/**
 * OAR Claim Analysis Command Interfaces
 * 定义权利要求分析相关的命令接口
 */

import type { ClaimAnalysisResponse } from '../types/claim-analysis';
import type { StatementComparisonOptions } from '../services/oar-task-service';

/**
 * 权利要求分析命令基础接口
 * 定义命令的基本操作
 */
export interface ClaimAnalysisCommand {
    /**
     * 执行命令
     *
     * @returns 执行结果
     */
    execute(): Promise<unknown>;

    /**
     * 检查命令是否可以执行
     *
     * @returns 是否可以执行
     */
    canExecute(): boolean;

    /**
     * 撤销命令（可选操作）
     */
    undo?(): Promise<void>;
}

/**
 * 分析权利要求命令接口
 */
export interface AnalyzeClaimsCommand extends ClaimAnalysisCommand {
    execute(): Promise<ClaimAnalysisResponse>;
}

/**
 * 生成陈述书内容命令接口
 */
export interface GenerateStatementContentCommand extends ClaimAnalysisCommand {
    execute(): Promise<string>;
}

/**
 * 生成对照页内容命令接口
 */
export interface GenerateComparisonPageCommand extends ClaimAnalysisCommand {
    execute(): Promise<string>;
}

/**
 * 权利要求分析命令工厂接口
 * 用于创建各种命令实例
 */
export interface ClaimAnalysisCommandFactory {
    /**
     * 创建分析权利要求命令
     *
     * @param taskId - 任务 ID
     * @param conclusiveOpinionId - 结论性意见 ID
     * @returns 分析命令实例
     */
    createAnalyzeCommand(taskId: string, conclusiveOpinionId: string): AnalyzeClaimsCommand;

    /**
     * 创建生成陈述书内容命令
     *
     * @param taskId - 任务 ID
     * @param claimAnalysisResults - 权利要求分析结果列表
     * @returns 生成陈述书命令实例
     */
    createGenerateStatementCommand(
        taskId: string,
        claimAnalysisResults: import('../types/claim-analysis').ClaimAnalysisResult[],
    ): GenerateStatementContentCommand;

    /**
     * 创建生成对照页内容命令
     *
     * @param taskId - 任务 ID
     * @param options - 对照页生成选项
     * @returns 生成对照页命令实例
     */
    createGenerateComparisonCommand(
        taskId: string,
        options: StatementComparisonOptions,
    ): GenerateComparisonPageCommand;
}

/**
 * 权利要求分析命令工厂依赖注入符号
 */
export const ClaimAnalysisCommandFactory = Symbol.for('ClaimAnalysisCommandFactory');
